/**
 * Sequence Analytics — per-step / per-variant funnels over email-log events.
 *
 * The missing measurement leg of the outreach loop: a PURE fold over
 * {@link EmailLogEvent}s (emitted by the governed send path + the reply
 * engine) into per-step, per-variant funnels, a reply-led variant winner,
 * and a sequence-health verdict that flags bounce spikes before they burn
 * mailbox reputation.
 *
 * MPP-safety is a design decision, not a footnote: Apple Mail Privacy
 * Protection auto-fires open pixels, so opens are inflated and unreliable.
 * {@link variantWinner} therefore decides on REPLY rates only — opens and
 * clicks are displayed, never used to crown a winner.
 *
 * Deliberately PURE: no store, no firebase, no clock. Every number is a
 * count of events the caller actually observed — nothing is fabricated.
 */

// ===========================================================================
// Events
// ===========================================================================

export type EmailLogEventKind =
  | 'sent'
  | 'delivered'
  | 'opened'
  | 'clicked'
  | 'replied'
  | 'meeting_booked'
  | 'bounced';

/** One observed email event, attributed to a sequence step (+ variant). */
export interface EmailLogEvent {
  kind: EmailLogEventKind;
  stepId: string;
  /** A/B variant label; absent for un-split steps. */
  variant?: string;
  contactId?: string;
  sequenceId?: string;
  /** ISO string or epoch ms; carried for display, not used in the fold. */
  at?: string | number;
  /** For 'replied' events: the classified intent ('interested' = positive). */
  intent?: string;
}

// ===========================================================================
// Funnels
// ===========================================================================

export interface StepFunnel {
  stepId: string;
  variant?: string;
  sent: number;
  delivered: number;
  opened: number;
  clicked: number;
  replied: number;
  positiveReplied: number;
  meetingBooked: number;
  bounced: number;
}

function emptyFunnel(stepId: string, variant?: string): StepFunnel {
  return {
    stepId,
    ...(variant !== undefined ? { variant } : {}),
    sent: 0,
    delivered: 0,
    opened: 0,
    clicked: 0,
    replied: 0,
    positiveReplied: 0,
    meetingBooked: 0,
    bounced: 0,
  };
}

/**
 * Fold events into one funnel per step+variant. Pure and total: unknown
 * event kinds are ignored, a 'replied' event with intent 'interested'
 * counts as BOTH replied and positiveReplied. Output is sorted by stepId,
 * then variant, so the table renders deterministically.
 */
export function computeStepFunnels(events: EmailLogEvent[]): StepFunnel[] {
  const byKey = new Map<string, StepFunnel>();
  for (const e of events) {
    if (!e.stepId) continue;
    const key = `${e.stepId}\u0000${e.variant ?? ''}`;
    let funnel = byKey.get(key);
    if (!funnel) {
      funnel = emptyFunnel(e.stepId, e.variant);
      byKey.set(key, funnel);
    }
    switch (e.kind) {
      case 'sent':
        funnel.sent++;
        break;
      case 'delivered':
        funnel.delivered++;
        break;
      case 'opened':
        funnel.opened++;
        break;
      case 'clicked':
        funnel.clicked++;
        break;
      case 'replied':
        funnel.replied++;
        if (e.intent === 'interested') funnel.positiveReplied++;
        break;
      case 'meeting_booked':
        funnel.meetingBooked++;
        break;
      case 'bounced':
        funnel.bounced++;
        break;
      default:
        break;
    }
  }
  return [...byKey.values()].sort((a, b) => {
    if (a.stepId !== b.stepId) return a.stepId < b.stepId ? -1 : 1;
    const va = a.variant ?? '';
    const vb = b.variant ?? '';
    return va < vb ? -1 : va > vb ? 1 : 0;
  });
}

// ===========================================================================
// Variant winner — reply-led, MPP-safe
// ===========================================================================

export type WinnerMetric = 'replyRate' | 'positiveReplyRate';

function rateOf(f: StepFunnel, metric: WinnerMetric): number {
  if (f.sent <= 0) return 0;
  return (metric === 'replyRate' ? f.replied : f.positiveReplied) / f.sent;
}

/**
 * Crown the winning variant per the chosen REPLY metric (never opens —
 * MPP-safe by construction). Considers only steps with 2+ labelled
 * variants; within a step a tie means no winner. Across steps the clearest
 * result (largest lift) wins. Returns null when no step has a strict
 * winner.
 *
 * `lift` is relative to the runner-up: (winner − runnerUp) / runnerUp.
 * When the runner-up converted at 0, the winner's absolute rate is
 * reported instead (a relative lift over zero is undefined, not infinite).
 */
export function variantWinner(
  funnels: StepFunnel[],
  metric: WinnerMetric,
): { stepId: string; winner: string; lift: number } | null {
  const bySt = new Map<string, StepFunnel[]>();
  for (const f of funnels) {
    if (f.variant === undefined) continue;
    const list = bySt.get(f.stepId) ?? [];
    list.push(f);
    bySt.set(f.stepId, list);
  }

  let best: { stepId: string; winner: string; lift: number } | null = null;
  for (const [stepId, variants] of bySt) {
    if (variants.length < 2) continue;
    const ranked = [...variants].sort((a, b) => rateOf(b, metric) - rateOf(a, metric));
    const top = rateOf(ranked[0], metric);
    const runnerUp = rateOf(ranked[1], metric);
    if (top <= runnerUp) continue; // tied (or empty) — no honest winner
    const lift = runnerUp > 0 ? (top - runnerUp) / runnerUp : top;
    if (best === null || lift > best.lift) {
      best = { stepId, winner: ranked[0].variant as string, lift };
    }
  }
  return best;
}

// ===========================================================================
// Sequence health
// ===========================================================================

/** Bounce rate above which mailbox reputation is genuinely at risk. */
export const BOUNCE_SPIKE_THRESHOLD = 0.02;

/** Delivery rate below which something upstream is broken. */
export const LOW_DELIVERY_THRESHOLD = 0.95;

/** Sends before a zero-reply sequence is called out as a copy problem. */
export const NO_REPLY_SAMPLE_SIZE = 50;

/**
 * Roll every funnel into sequence-level health: bounce rate, reply rate,
 * and plain-English warnings. Empty input (or zero sends) is honestly
 * healthy-by-absence: zero rates, zero warnings — never invented risk.
 */
export function sequenceHealth(funnels: StepFunnel[]): {
  bounceRate: number;
  replyRate: number;
  warnings: string[];
} {
  let sent = 0;
  let delivered = 0;
  let replied = 0;
  let bounced = 0;
  for (const f of funnels) {
    sent += f.sent;
    delivered += f.delivered;
    replied += f.replied;
    bounced += f.bounced;
  }
  if (sent === 0) return { bounceRate: 0, replyRate: 0, warnings: [] };

  const bounceRate = bounced / sent;
  const replyRate = replied / sent;
  const warnings: string[] = [];

  if (bounceRate > BOUNCE_SPIKE_THRESHOLD) {
    warnings.push(
      `Bounce spike: ${(bounceRate * 100).toFixed(1)}% of sends bounced (threshold ${(BOUNCE_SPIKE_THRESHOLD * 100).toFixed(0)}%) — pause sends and clean the list before mailbox reputation burns.`,
    );
  }
  if (delivered > 0 && delivered / sent < LOW_DELIVERY_THRESHOLD) {
    warnings.push(
      `Low delivery: only ${((delivered / sent) * 100).toFixed(1)}% of sends confirmed delivered (threshold ${(LOW_DELIVERY_THRESHOLD * 100).toFixed(0)}%) — check domain/SPF/DKIM health.`,
    );
  }
  if (replied === 0 && sent >= NO_REPLY_SAMPLE_SIZE) {
    warnings.push(
      `No replies across ${sent} sends — the copy or targeting is not landing; rotate the sequence before sending more.`,
    );
  }
  return { bounceRate, replyRate, warnings };
}
